import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import SiteFooter from '@/components/feature/SiteFooter';
import usePageSchema from '@/hooks/usePageSchema';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://novaaiwebsites.com';

const services = [
  {
    num: '01',
    title: 'Custom Web Design',
    description: 'We design and build high-converting, mobile-responsive websites tailored to your brand. Every site is custom-crafted — no templates, no shortcuts. Fast, SEO-optimized, and live in under 24 hours.',
    features: ['Custom design & development', 'Mobile-first responsive', 'SEO optimization', 'Live in under 24 hours', 'Free revisions'],
  },
  {
    num: '02',
    title: 'AI Receptionist',
    description: 'Our AI receptionist answers your phone 24/7, handles inquiries, books appointments, and qualifies leads — so you never miss a potential customer, even when you\'re unavailable.',
    features: ['24/7 call answering', 'Appointment booking', 'Lead qualification', 'Natural conversation AI', 'CRM integration'],
  },
  {
    num: '03',
    title: 'E-Commerce Development',
    description: 'Full-featured online stores built to sell. We handle everything from product pages and checkout flows to inventory management and payment processing.',
    features: ['Custom storefront design', 'Secure checkout', 'Inventory management', 'Payment gateway setup', 'Product photography guidance'],
  },
  {
    num: '04',
    title: 'AI Automation',
    description: 'Intelligent workflows that handle the repetitive tasks draining your time. Automated follow-ups, SMS/chat bots, and smart appointment scheduling — all without manual effort.',
    features: ['SMS & chat automation', 'Automated follow-ups', 'Smart scheduling', 'Lead capture bots', 'Performance reporting'],
  },
  {
    num: '05',
    title: 'Google Business Optimization',
    description: 'Dominate local search results with a fully optimized Google Business Profile. We handle setup, optimization, review management, and ongoing updates to keep you at the top.',
    features: ['Profile setup & optimization', 'Review management', 'Local SEO strategy', 'Citation building', 'Monthly reporting'],
  },
  {
    num: '06',
    title: 'Social Media Management',
    description: 'Consistent, engaging content that builds your brand and drives followers to customers. We create, schedule, and manage your social presence across all major platforms.',
    features: ['Content creation & scheduling', 'Platform management', 'Engagement & community', 'Analytics reporting', 'Ad campaign setup'],
  },
];

const servicesSchemas = [
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'Services', item: `${siteUrl}/services` },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${siteUrl}/services#webpage`,
    url: `${siteUrl}/services`,
    name: 'Web Design & AI Services Southern California | NOVA AI DIGITAL',
    description: 'Custom web design, AI receptionist, e-commerce, AI automation, Google Business optimization & social media management for Southern California businesses.',
    inLanguage: 'en-US',
    isPartOf: { '@id': `${siteUrl}/#website` },
    provider: { '@id': `${siteUrl}/#business` },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'NOVA AI DIGITAL Services',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        item: {
          '@type': 'Service',
          name: 'Custom Web Design',
          description: 'We design and build high-converting, mobile-responsive websites tailored to your brand. Custom-crafted, fast, SEO-optimized, and live in under 24 hours.',
          provider: { '@id': `${siteUrl}/#business` },
          areaServed: 'Southern California',
          serviceType: 'Web Design',
        },
      },
      {
        '@type': 'ListItem',
        position: 2,
        item: {
          '@type': 'Service',
          name: 'AI Receptionist',
          description: 'Our AI receptionist answers your phone 24/7, handles inquiries, books appointments, and qualifies leads automatically.',
          provider: { '@id': `${siteUrl}/#business` },
          areaServed: 'Southern California',
          serviceType: 'AI Automation',
        },
      },
      {
        '@type': 'ListItem',
        position: 3,
        item: {
          '@type': 'Service',
          name: 'E-Commerce Development',
          description: 'Full-featured online stores built to sell, covering product pages, checkout flows, inventory management and payment processing.',
          provider: { '@id': `${siteUrl}/#business` },
          areaServed: 'Southern California',
          serviceType: 'E-Commerce',
        },
      },
      {
        '@type': 'ListItem',
        position: 4,
        item: {
          '@type': 'Service',
          name: 'AI Automation',
          description: 'Intelligent workflows handling automated follow-ups, SMS/chat bots, and smart appointment scheduling.',
          provider: { '@id': `${siteUrl}/#business` },
          areaServed: 'Southern California',
          serviceType: 'AI Automation',
        },
      },
      {
        '@type': 'ListItem',
        position: 5,
        item: {
          '@type': 'Service',
          name: 'Google Business Optimization',
          description: 'Dominate local search results with a fully optimized Google Business Profile, review management, and local SEO strategy.',
          provider: { '@id': `${siteUrl}/#business` },
          areaServed: 'Southern California',
          serviceType: 'SEO',
        },
      },
      {
        '@type': 'ListItem',
        position: 6,
        item: {
          '@type': 'Service',
          name: 'Social Media Management',
          description: 'Consistent, engaging content that builds your brand across all major social platforms with analytics and ad campaign setup.',
          provider: { '@id': `${siteUrl}/#business` },
          areaServed: 'Southern California',
          serviceType: 'Social Media Marketing',
        },
      },
    ],
  },
];

export default function ServicesPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  usePageSchema(servicesSchemas, 'services');

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main>
        {/* Hero heading */}
        <div className="h-20" />
        <div className="px-6 md:px-12 lg:px-16 pt-10 pb-8">
          <h1
            className="text-black leading-none"
            style={{
              fontFamily: 'Syne, sans-serif',
              fontWeight: 800,
              fontSize: 'clamp(3.5rem, 9vw, 8rem)',
              letterSpacing: '-0.02em',
            }}
          >
            Our Services
          </h1>
        </div>

        {/* Intro row */}
        <div className="px-6 md:px-12 lg:px-16 py-10 border-t border-b border-black/8 flex flex-col sm:flex-row items-start justify-between gap-6">
          <p className="text-black/50 text-sm md:text-base leading-relaxed max-w-lg" style={{ fontFamily: 'Inter, sans-serif' }}>
            From custom websites to AI-powered automation — everything your business needs
            to grow its digital presence and convert more leads.
          </p>
          <Link
            to="/contact"
            className="border border-black text-black text-xs uppercase tracking-widest px-8 py-4 hover:bg-black hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap"
            style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '0.14em' }}
          >
            Get a Quote
          </Link>
        </div>

        {/* Service list */}
        <div>
          {services.map((svc, i) => (
            <div
              key={svc.num}
              className="px-6 md:px-12 lg:px-16 py-14 md:py-20 border-b border-black/8"
            >
              <div className="flex flex-col md:flex-row gap-8 md:gap-20">
                <div className="md:w-2/5">
                  <span className="text-xs text-black/30 mb-4 block" style={{ fontFamily: 'Inter, sans-serif' }}>{svc.num}</span>
                  <h2
                    className="text-black leading-tight"
                    style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 'clamp(1.5rem, 3vw, 2.8rem)', letterSpacing: '-0.01em' }}
                  >
                    {svc.title}
                  </h2>
                </div>
                <div className="md:w-3/5 flex flex-col gap-6">
                  <p className="text-black/60 text-sm md:text-base leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {svc.description}
                  </p>
                  <ul className="flex flex-col gap-2">
                    {svc.features.map(f => (
                      <li key={f} className="flex items-center gap-3 text-sm text-black/70" style={{ fontFamily: 'Inter, sans-serif' }}>
                        <span className="w-1 h-1 bg-black rounded-full flex-shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="px-6 md:px-12 lg:px-16 py-16 md:py-24 flex flex-col items-center text-center gap-6">
          <h2
            className="text-black"
            style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 'clamp(1.8rem, 4vw, 3.5rem)', letterSpacing: '-0.02em' }}
          >
            Ready to get started?
          </h2>
          <Link
            to="/contact"
            className="border border-black text-black text-xs uppercase tracking-widest px-10 py-4 hover:bg-black hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap"
            style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '0.14em' }}
          >
            Contact Us
          </Link>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
